import React from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Building2, FileText, User, X } from 'lucide-react';

export function DrugTestComparison({ check, verificationData, setVerificationData, handleFieldVerification }) {
    const candidateData = {
      ...(check?.subcheck_data || {}),
      father_name: check?.father_name || check?.subcheck_data?.father_name || '',
      dob: check?.dob || check?.subcheck_data?.dob || check?.subcheck_data?.date_of_birth || '',
      candidate_name: check?.subcheck_data?.candidate_name || check?.subcheck_data?.name || check?.employee_name || '',
    };
    
    // Verification status options with color codes
    const verificationStatusOptions = [
      { value: 'verified', label: 'Verified', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50 border-green-300' },
      { value: 'discrepancy', label: 'Discrepancy', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-300' },
      { value: 'partially_verified', label: 'Partially Verified', color: 'bg-sky-400', textColor: 'text-sky-700', bgLight: 'bg-sky-50 border-sky-300' },
      { value: 'not_verified', label: 'Not Verified', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 border-amber-300' },
    ];
    
    const getStatusClasses = (status) => {
      const found = verificationStatusOptions.find(o => o.value === status);
      return found || { color: 'bg-gray-300', textColor: 'text-gray-600', bgLight: 'bg-white border-gray-200' };
    };
    
    // Drug test panels
    const drugPanels = [
      { id: 'amphetamines', label: 'Amphetamines (AMP)', description: 'Stimulants' },
      { id: 'barbiturates', label: 'Barbiturates (BAR)', description: 'Sedatives' },
      { id: 'benzodiazepines', label: 'Benzodiazepines (BZO)', description: 'Anti-anxiety' },
      { id: 'cocaine', label: 'Cocaine (COC)', description: 'Stimulant' },
      { id: 'marijuana', label: 'Marijuana/THC (THC)', description: 'Cannabis' },
      { id: 'methamphetamine', label: 'Methamphetamine (MET)', description: 'Stimulant' },
      { id: 'opiates', label: 'Opiates (OPI)', description: 'Painkillers' },
      { id: 'phencyclidine', label: 'Phencyclidine (PCP)', description: 'Hallucinogen' },
      { id: 'methadone', label: 'Methadone (MTD)', description: 'Opioid treatment' },
      { id: 'propoxyphene', label: 'Propoxyphene (PPX)', description: 'Painkiller' },
    ];
    
    const testResultOptions = [
      { value: 'negative', label: 'Negative (Clear)', color: 'text-green-600 font-bold' },
      { value: 'positive', label: 'Positive (Detected)', color: 'text-red-600 font-bold' },
      { value: 'inconclusive', label: 'Inconclusive', color: 'text-amber-600' },
      { value: 'not_tested', label: 'Not Tested', color: 'text-slate-500' },
    ];
    
    // Clear form function
    const clearDrugTestForm = () => {
      const clearedFields = {};
      drugPanels.forEach(panel => {
        clearedFields[`drug_${panel.id}`] = '';
      });
      setVerificationData(prev => ({
        ...prev,
        verified_details: {
          ...prev.verified_details,
          ...clearedFields,
          candidate_name: '',
          sample_collection_date: '',
          sample_type: '',
          collection_site: '',
          collector_name: '',
          chain_of_custody_number: '',
          lab_name: '',
          lab_accreditation: '',
          test_date: '',
          report_date: '',
          mro_name: '',
          mro_contact: '',
          overall_result: '',
          additional_remarks: '',
        }
      }));
      toast.info('Form cleared');
    };
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Drug Test Verification - {check?.subcheck_type || 'Drug Screening'}
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearDrugTestForm}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Form
          </Button>
        </div>
        
        {/* Status Legend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
          <span className="text-xs font-medium text-slate-600">Result Legend:</span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-xs text-green-700">Negative (Clear)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-xs text-red-700">Positive (Detected)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-xs text-amber-700">Inconclusive</span>
            </div>
          </div>
        </div>
        
        {/* Candidate & Sample Details */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-2 border-blue-200 shadow-md">
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Candidate Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                <span className="text-blue-600 text-xs font-medium">Candidate Name</span>
                <p className="font-semibold">{candidateData.candidate_name || check?.employee_name || '-'}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <span className="text-blue-600 text-xs font-medium">Date of Birth</span>
                  <p className="font-semibold text-sm">{candidateData.dob || '-'}</p>
                </div>
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <span className="text-blue-600 text-xs font-medium">Gender</span>
                  <p className="font-semibold text-sm">{candidateData.gender || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-teal-200 shadow-md">
            <CardHeader className="pb-2 bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Sample Collection Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Collection Date</Label>
                  <Input 
                    type="date"
                    value={verificationData.verified_details.sample_collection_date || ''} 
                    onChange={(e) => handleFieldVerification('sample_collection_date', true, e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Sample Type</Label>
                  <Select 
                    value={verificationData.verified_details.sample_type || ''} 
                    onValueChange={(v) => handleFieldVerification('sample_type', true, v)}
                  >
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urine">Urine</SelectItem>
                      <SelectItem value="blood">Blood</SelectItem>
                      <SelectItem value="hair">Hair</SelectItem>
                      <SelectItem value="saliva">Saliva/Oral Fluid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Collection Site</Label>
                <Input 
                  value={verificationData.verified_details.collection_site || ''} 
                  onChange={(e) => handleFieldVerification('collection_site', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="Lab/Hospital name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Chain of Custody #</Label>
                <Input 
                  value={verificationData.verified_details.chain_of_custody_number || ''} 
                  onChange={(e) => handleFieldVerification('chain_of_custody_number', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="COC number"
                />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Drug Panel Results */}
        <Card className="border-2 border-red-200 shadow-md">
          <CardHeader className="pb-2 bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-t-lg">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Drug Panel Test Results (10-Panel)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 bg-gradient-to-b from-red-50 to-white">
            <div className="grid grid-cols-2 gap-3">
              {drugPanels.map(panel => (
                <div 
                  key={panel.id} 
                  className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
                    verificationData.verified_details[`drug_${panel.id}`] === 'negative' 
                      ? 'bg-green-50 border-green-300' 
                      : verificationData.verified_details[`drug_${panel.id}`] === 'positive'
                        ? 'bg-red-50 border-red-300'
                        : verificationData.verified_details[`drug_${panel.id}`] === 'inconclusive'
                          ? 'bg-amber-50 border-amber-300'
                          : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">{panel.label}</Label>
                      <p className="text-xs text-slate-500">{panel.description}</p>
                    </div>
                    <Select 
                      value={verificationData.verified_details[`drug_${panel.id}`] || ''} 
                      onValueChange={(v) => handleFieldVerification(`drug_${panel.id}`, true, v)}
                    >
                      <SelectTrigger className="h-8 w-36 text-xs border-slate-300">
                        <SelectValue placeholder="Result" />
                      </SelectTrigger>
                      <SelectContent>
                        {testResultOptions.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            <span className={o.color}>{o.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Lab & MRO Details */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-2 border-purple-200 shadow-md">
            <CardHeader className="pb-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Laboratory Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <div className="space-y-1">
                <Label className="text-xs font-medium">Lab Name</Label>
                <Input 
                  value={verificationData.verified_details.lab_name || ''} 
                  onChange={(e) => handleFieldVerification('lab_name', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="Laboratory name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Lab Accreditation</Label>
                <Select 
                  value={verificationData.verified_details.lab_accreditation || ''} 
                  onValueChange={(v) => handleFieldVerification('lab_accreditation', true, v)}
                >
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nabl_accredited">NABL Accredited</SelectItem>
                    <SelectItem value="cap_accredited">CAP Accredited</SelectItem>
                    <SelectItem value="samhsa_certified">SAMHSA Certified</SelectItem>
                    <SelectItem value="iso_certified">ISO Certified</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Test Date</Label>
                  <Input 
                    type="date"
                    value={verificationData.verified_details.test_date || ''} 
                    onChange={(e) => handleFieldVerification('test_date', true, e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Report Date</Label>
                  <Input 
                    type="date"
                    value={verificationData.verified_details.report_date || ''} 
                    onChange={(e) => handleFieldVerification('report_date', true, e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-indigo-200 shadow-md">
            <CardHeader className="pb-2 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Medical Review Officer (MRO)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <div className="space-y-1">
                <Label className="text-xs font-medium">MRO Name</Label>
                <Input 
                  value={verificationData.verified_details.mro_name || ''} 
                  onChange={(e) => handleFieldVerification('mro_name', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="Dr. Name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">MRO Contact</Label>
                <Input 
                  value={verificationData.verified_details.mro_contact || ''} 
                  onChange={(e) => handleFieldVerification('mro_contact', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="Phone/Email"
                />
              </div>
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
                verificationData.verified_details.overall_result === 'negative' 
                  ? 'bg-green-50 border-green-300' 
                  : verificationData.verified_details.overall_result === 'positive'
                    ? 'bg-red-50 border-red-300'
                    : 'bg-white border-indigo-100'
              }`}>
                <Label className="text-xs font-semibold text-slate-700 mb-2 block">Overall Result</Label>
                <Select 
                  value={verificationData.verified_details.overall_result || ''} 
                  onValueChange={(v) => handleFieldVerification('overall_result', true, v)}
                >
                  <SelectTrigger className="h-8 text-sm border-slate-300">
                    <SelectValue placeholder="Select overall result" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="negative"><span className="text-green-600 font-bold">NEGATIVE - All Clear</span></SelectItem>
                    <SelectItem value="positive"><span className="text-red-600 font-bold">POSITIVE - Substance Detected</span></SelectItem>
                    <SelectItem value="inconclusive"><span className="text-amber-600">INCONCLUSIVE - Retest Required</span></SelectItem>
                    <SelectItem value="dilute_negative"><span className="text-blue-600">DILUTE NEGATIVE</span></SelectItem>
                    <SelectItem value="dilute_positive"><span className="text-red-500">DILUTE POSITIVE</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Additional Remarks */}
        <Card className="border-2 border-slate-200">
          <CardHeader className="pb-2 bg-slate-100">
            <CardTitle className="text-sm text-slate-700">Additional Remarks</CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <Textarea 
              value={verificationData.verified_details.additional_remarks || ''} 
              onChange={(e) => handleFieldVerification('additional_remarks', true, e.target.value)}
              className="h-20 text-sm resize-none"
              placeholder="Any additional observations, MRO comments, or follow-up requirements..."
            />
          </CardContent>
        </Card>
      </div>
    );
}
